import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";

import { useAsu } from "@framework/provider-asu";
import { useApiCall } from "@framework/react-hooks";

import { InfoDialogButton } from "../info-dialog";
import { TrainDetailsTable } from "./table";
import type {
  ITrainComponent,
  ITrainComponentResponse,
  ITrainDetailsResponse,
  ITrainMechanism,
  ITrainMechanismResponse,
} from "./interfaces";

export const TrainDetailsButton: FC = () => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const { selectedItem } = useAsu();
  const trainId = selectedItem && "TrainId" in selectedItem && selectedItem.TrainId;

  const [trainDetails, setTrainDetails] = useState<any>([]);

  const transformDetails = (details: ITrainDetailsResponse): ITrainMechanism[] => {
    const result: ITrainMechanism[] = [];

    details?.TrainMechanisms?.forEach((mechanism: ITrainMechanismResponse) => {
      const { idTrainMech: id, Name: name, Description: description = "-", Mech_Models = [] } = mechanism;

      const trainMechanism: ITrainMechanism = { id, name, description, standardSize: "-", components: [] };

      if (
        Mech_Models?.length &&
        Mech_Models[0]?.Mech_StandardSizes?.length &&
        Mech_Models[0].Mech_StandardSizes[0].Name
      ) {
        trainMechanism.standardSize = Mech_Models[0].Mech_StandardSizes[0].Name;
      }

      result.push(trainMechanism);
    });

    details?.TrainComponents?.forEach((component: ITrainComponentResponse) => {
      const {
        idTrainComponent: id,
        idTrainMechanism,
        StandardSize: standardSize = "-",
        Name: name,
        Description: description = "-",
      } = component;

      const trainComponent: ITrainComponent = { id, name, description, standardSize };

      if (idTrainMechanism) {
        const foundMechanism = result.find(({ id }) => id === idTrainMechanism);
        foundMechanism?.components.push(trainComponent);
      } else {
        const notBoundName = formatMessage({ id: "pages.scheme.trainScheme.details.headers.notBound" });
        let foundNotBound = result.find(({ name }) => name === notBoundName);

        if (!foundNotBound) {
          const notBound = {
            id: 0,
            name: notBoundName,
            description: "",
            components: [],
          };
          result.push(notBound);

          foundNotBound = result.find(({ name }) => name === notBoundName);
        }

        foundNotBound?.components.push(trainComponent);
      }
    });

    return result;
  };

  const { fn: getTrainDetails, isLoading: isTrainDetailsLoading } = useApiCall(
    (api, { trainId }: { trainId: number }) => {
      return api
        .fetchJson({
          url: `/trains/${trainId}/details`,
          method: "GET",
        })
        .then((json: ITrainDetailsResponse[] | null) => {
          if (!json) {
            return;
          }

          if (!json.length) {
            enqueueSnackbar(formatMessage({ id: "pages.scheme.trainScheme.details.infoNotFound" }), {
              variant: "warning",
            });
            return;
          }
          setTrainDetails(transformDetails(json[0]));
        });
    },
    { success: false },
  );

  useEffect(() => {
    if (trainId) {
      void getTrainDetails(undefined, { trainId });
    }
  }, [trainId]);

  return (
    <Box sx={{ ml: 1 }}>
      <InfoDialogButton title={"pages.scheme.trainScheme.details.title"} maxWidth="xl">
        <TrainDetailsTable details={trainDetails || []} loading={isTrainDetailsLoading} />
      </InfoDialogButton>
    </Box>
  );
};
